import { Box } from "@material-ui/core"
import { BannerResponse } from "api"
import { Desktop, Mobile, useIsDesktop } from "common/MediaQueries"
import Image from "next/image"
import { Dispatch, SetStateAction, useCallback } from "react"
import styled, { css } from "styled-components"
import SwiperCore, { A11y, Autoplay, Keyboard, Pagination } from "swiper"
import "swiper/css"
import "swiper/css/bundle"
import "swiper/css/pagination"
import { Swiper, SwiperSlide } from "swiper/react"
import { fadeIn, fadeInText } from "theme/animation"
import MainBannerActions from "./MainBannerActions"
import MainBannerText from "./MainBannerText"
import PauseButton from "./PauseButton"

const StyledSwiper = styled(Swiper)`
  width: 100%;
  height: 100%;

  .swiper-slide {
    text-align: center;
    font-size: 18px;
    display: flex;
    justify-content: space-evenly;
    align-items: center;
    ${(p) => p.theme.breakpoints.down("sm")} {
      flex-direction: column;
      > div {
        width: 100%;
      }
    }
  }

  .swiper-pagination {
    bottom: 0;
    ${(p) => p.theme.breakpoints.up("md")} {
      bottom: 5px;
    }

    .swiper-pagination-bullet-active {
      background: var(--Green, #264f36);
    }
  }

  .swiper-pagination-bullet {
    height: 6px;
    width: 6px;
    border-radius: 200px;
    border: 1px solid var(--Green, #264f36);
    background: var(--White, #fff);

    :hover {
      box-shadow: 0 0 0 2px #cecdcd;
    }
  }

  .swiper-pagination-bullet-active {
    width: 13px;
    height: 6px;
    border-radius: 200px;
  }
`

const StyledSwipperWrapper = styled(Box)`
  ${(p) => p.theme.breakpoints.up("md")} {
    width: 500px;
  }
`

const StyledImagesWrapper = styled(Box)`
  display: flex;
  height: 450px;
  width: 100%;
  justify-content: center;
  max-width: 1160px;
`

const StyledBannerTransition = styled.div<{ $active: boolean }>`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 602px;
  /* animation-name: ${(p) => p.$active && fadeInText};
  animation-duration: 2s;
  animation-delay: 1s; */
  position: relative;
  transition: opacity 0.5s ease-in-out, transform 3s ease-in-out;
  opacity: 0;
  transform: translate3d(0, -500px, 0);
  ${(p) =>
    p.$active &&
    css`
      opacity: 1;
      transform: translate3d(0, 0, 0);
      transition: all 1.5s 0.5s ease-in-out;
    `}
`

const StyledImageTransition = styled.div<{ $active: boolean }>`
  display: flex;
  height: 450px;
  width: 100%;
  margin-right: 60px;
  justify-content: center;
  /* animation-duration: 1s; */
  /* animation-name: ${(p) => p.$active && fadeIn};
  animation-duration: 2s;
  animation-delay: 1s; */
  pointer-events: none;
  background: #fff;
  transition: all 0.5s ease-in-out; // Modified transition duration to 1 second
  opacity: 0;

  transform: translate3d(-500px, 0, 0);
  ${(p) =>
    p.$active &&
    css`
      opacity: 1;
      transform: translate3d(0, 0, 0);
      transition: all 1.5s 0.5s ease-in-out;
    `}

  ${(p) => p.theme.breakpoints.up("xl")} {
    margin-right: 120px;
  }
`

const StyledImageWrapp = styled.div`
  width: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-repeat: no-repeat;
  position: relative;
  pointer-events: none;
`

const StyledDiv = styled.div`
  ${(p) => p.theme.breakpoints.down("sm")} {
    height: 260px;
    position: relative;
    width: 100%;
    background-repeat: no-repeat;
    background-position: top;
    background-size: contain;
  }
`

const StyledMobileImageTransition = styled.div<{ $active: boolean }>`
  height: 260px;
  width: 100%;
  animation-name: ${(p) => p.$active && fadeIn};
  animation-duration: 2s;
  padding: 0 16px;
`

const StyledTextTransition = styled.div<{ $active: boolean }>`
  animation-name: ${(p) => p.$active && fadeInText};
  animation-duration: 2s;
`

type BannerSliderProps = {
  bannerData: BannerResponse[]
  divRef: React.RefObject<HTMLDivElement>
  setImgUrl: Dispatch<SetStateAction<string>>
}

SwiperCore.use([Autoplay])

const MainBannerSlider = ({
  bannerData,
  divRef,
  setImgUrl,
}: BannerSliderProps) => {
  const isDesktop = useIsDesktop()
  const handleSlideChange = useCallback(
    (swiper) => {
      const activeIndex = swiper.activeIndex
      const imageKey = isDesktop ? "leftImage" : "backgroundMobileImage"
      setImgUrl(bannerData[activeIndex]?.[imageKey]?.imageUrl)
    },
    [bannerData, setImgUrl, isDesktop]
  )

  return (
    <StyledSwiper
      spaceBetween={30}
      pagination={{
        clickable: true,
      }}
      speed={1000}
      modules={[Pagination, Keyboard, A11y]}
      autoplay={{
        delay: 5000,
        disableOnInteraction: false,
      }}
      a11y={{
        enabled: true,
        prevSlideMessage: "Previous slide",
        nextSlideMessage: "Next slide",
        firstSlideMessage: "This is the first slide",
        lastSlideMessage: "This is the last slide",
        paginationBulletMessage: "מידע אודות הגיפטקארד הבא / הקודם",
      }}
      keyboard={{
        enabled: true,
        onlyInViewport: false,
      }}
      onSlideChange={handleSlideChange}
    >
      {bannerData?.map((banner, index) => {
        return (
          <SwiperSlide key={index}>
            {({ isActive }) => {
              return (
                <>
                  <Mobile aboveTheFold>
                    <StyledMobileImageTransition $active={isActive}>
                      <StyledDiv>
                        {banner.mobileImage.imageUrl ? (
                          <Image
                            src={banner.mobileImage.imageUrl}
                            objectFit="contain"
                            layout="fill"
                          />
                        ) : (
                          <></>
                        )}
                      </StyledDiv>
                    </StyledMobileImageTransition>
                  </Mobile>
                  <Desktop aboveTheFold>
                    <StyledImagesWrapper>
                      <StyledBannerTransition $active={isActive}>
                        <StyledSwipperWrapper>
                          <MainBannerText banner={banner} />
                          <MainBannerActions divRef={divRef} banner={banner} />
                        </StyledSwipperWrapper>
                      </StyledBannerTransition>

                      <StyledImageTransition $active={isActive}>
                        <StyledImageWrapp>
                          {banner?.image?.imageUrl ? (
                            <Image
                              src={banner?.image?.imageUrl}
                              alt={banner?.image?.alt}
                              layout="fill"
                              objectFit="contain"
                            />
                          ) : (
                            <></>
                          )}
                        </StyledImageWrapp>
                      </StyledImageTransition>
                    </StyledImagesWrapper>
                  </Desktop>
                  <Mobile aboveTheFold>
                    <StyledSwipperWrapper>
                      <StyledTextTransition $active={isActive}>
                        <MainBannerText banner={banner} />
                      </StyledTextTransition>
                      <MainBannerActions divRef={divRef} banner={banner} />
                    </StyledSwipperWrapper>
                  </Mobile>
                </>
              )
            }}
          </SwiperSlide>
        )
      })}
      <PauseButton numSlides={bannerData.length} />
    </StyledSwiper>
  )
}

export default MainBannerSlider
